import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import logolight from "../../assets/images/09logo.png";
import logodark from "../../assets/images/09logo.png";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import withRouter from "../../components/Common/withRouter";
import OTPVerityLogin from "./OTPVerifyLogin";
import * as Yup from "yup";
import { useFormik } from "formik";
import { authLogin } from "../../store/actions/authentication";
import { createSelector } from "reselect";

const Login = (props) => {
  document.title = "Login | 09 TAX";

  const [showPassword, setShowPassword] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({
    mobile: "",
    password: "",
  });
  const [loginResponse, setLoginResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      mobile: "",
      password: "",
    },
    validationSchema: Yup.object({
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Please Enter Your mobile"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      setLoginCredentials(values);

      try {
        const response = await dispatch(
          authLogin(
            {
              mobile: values.mobile?.toString(),
              password: values.password,
            },
            props.router.navigate,
          ),
        );

        if (
          response &&
          response.code === 1 &&
          response.status === "Success" &&
          response.message === "OTP Sent to your mobile"
        ) {
          setLoginResponse(response);
          setShowOTPModal(true);
          setIsLoading(false);
        }
      } catch (error) {
        resetForm();
      } finally {
        setIsLoading(false);
      }
    },
  });

  const loginpage = createSelector(
    (state) => state.login,
    (state) => ({
      error: state.error,
    }),
  );

  const { error } = useSelector(loginpage);

  const handleOTPVerification = async (otp) => {
    setIsLoading(true);

    try {
      const otpVerificationData = {
        mobile: loginCredentials.mobile?.toString(),
        password: loginCredentials.password,
        otp: otp.toString(), // Ensure it's a string
      };

      const response = await dispatch(
        authLogin(otpVerificationData, props.router.navigate),
      );

      // Check for successful login with tokens
      if (response && response.access_token && response.refresh_token) {
        setShowOTPModal(false);
        navigate("/dashboard");
        return { success: true };
      } else {
        return {
          success: false,
          message: response?.message || "OTP verification failed",
        };
      }
    } catch (error) {
      return { success: false, message: "OTP verification failed" };
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPModalClose = () => {
    setShowOTPModal(false);
    setLoginCredentials({ mobile: "", password: "" });
    setLoginResponse(null);
  };

  const handleResendOTP = async () => {
    setIsLoading(true);

    try {
      const response = await dispatch(
        authLogin(
          {
            mobile: loginCredentials.mobile?.toString(),
            password: loginCredentials.password,
          },
          props.router.navigate,
        ),
      );

      if (response && response.code === 1 && response.status === "Success") {
        return { success: true, message: "OTP resent successfully" };
      } else {
        return { success: false, message: "Failed to resend OTP" };
      }
    } catch (error) {
      return { success: false, message: "Failed to resend OTP" };
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.body.className = "bg-pattern";
    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  return (
    <>
      <React.Fragment>
        <div className="bg-overlay"></div>
        <div className="account-pages my-5 pt-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={6} md={8} xl={4}>
                <Card>
                  <CardBody className="p-4">
                    <div>
                      <div className="text-center">
                        <Link to="/">
                          <img
                            src={logodark}
                            alt=""
                            height="100"
                            className="auth-logo logo-dark mx-auto"
                          />
                          <img
                            src={logolight}
                            alt=""
                            height="100"
                            className="auth-logo logo-light mx-auto"
                          />
                        </Link>
                      </div>
                      <h4 className="font-size-18 text-muted mt-2 text-center">
                        Welcome Back !
                      </h4>
                      <p className="mb-5 text-center">
                        Sign in to continue to 09 TAX.
                      </p>
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        {error ? (
                          <Alert color="danger">
                            <div>{error}</div>
                          </Alert>
                        ) : null}
                        <Row>
                          <Col md={12}>
                            <div className="mb-4">
                              <Label className="form-label">Mobile</Label>
                              <Input
                                name="mobile"
                                className="form-control"
                                placeholder="Enter Mobile Number"
                                type="number"
                                onInput={(e) => {
                                  if (e.target.value.length > 10) {
                                    e.target.value = e.target.value.slice(
                                      0,
                                      10,
                                    );
                                  }
                                }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.mobile || ""}
                                invalid={
                                  validation.touched.mobile &&
                                  validation.errors.mobile
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.mobile &&
                              validation.errors.mobile ? (
                                <FormFeedback type="invalid">
                                  <div>{validation.errors.mobile}</div>
                                </FormFeedback>
                              ) : null}
                            </div>
                            <div className="mb-4">
                              <Label className="form-label">Password</Label>
                              <div className="position-relative">
                                <Input
                                  name="password"
                                  value={validation.values.password || ""}
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Enter Password"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  invalid={
                                    validation.touched.password &&
                                    validation.errors.password
                                      ? true
                                      : false
                                  }
                                />
                                <button
                                  type="button"
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  onClick={togglePasswordVisibility}
                                  style={{
                                    height: "100%",
                                    border: "none",
                                    background: "transparent",
                                    zIndex: 10,
                                  }}
                                >
                                  <i
                                    className={`mdi ${
                                      showPassword ? "mdi-eye-off" : "mdi-eye"
                                    }`}
                                  ></i>
                                </button>
                                {validation.touched.password &&
                                validation.errors.password ? (
                                  <FormFeedback type="invalid">
                                    <div> {validation.errors.password} </div>
                                  </FormFeedback>
                                ) : null}
                              </div>
                            </div>

                            <Row>
                              <Col>
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="customControlInline"
                                  />
                                  <label
                                    className="form-label form-check-label"
                                    htmlFor="customControlInline"
                                  >
                                    Remember me
                                  </label>
                                </div>
                              </Col>
                            </Row>
                            <div className="d-grid mt-4">
                              <button
                                className="btn btn-primary waves-effect waves-light"
                                type="submit"
                                disabled={isLoading}
                                style={{ backgroundColor: "#003F8C" }}
                              >
                                {isLoading ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                    Logging In...
                                  </>
                                ) : (
                                  "Log In"
                                )}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
                <div className="mt-5 text-center">
                  <p className="text-white-50">
                    © {new Date().getFullYear()} 09 TAX. Crafted with{" "}
                    <i className="mdi mdi-heart text-danger"></i> by{" "}
                    <a
                      href="https://www.sswebcreation.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      SS WEB CREATION
                    </a>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>

      <OTPVerityLogin
        isOpen={showOTPModal}
        toggle={handleOTPModalClose}
        mobile={loginCredentials.mobile}
        onVerifyOTP={handleOTPVerification}
        onResendOTP={handleResendOTP}
        loginResponse={loginResponse}
        isLoading={isLoading}
      />
    </>
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};
